import { useState } from 'react';

import { useGlobalError } from 'hooks/useGlobalError';
import { useAuth } from 'providers/AuthProvider';
import { authService } from 'services/auth/authService';
import { userService } from 'services/user/userService';
import { clearLastSignUpEmail } from 'utils/localStorageData';

import { VerifyEmailType } from './verifyEmail.types';

type VerificationStatusType = 'notExecuted' | 'inProgress' | 'completed' | 'failed';

export const useVerifyEmail = (
  credentials: { email: string; password?: string },
  onConfirmed: () => void,
  type: VerifyEmailType = 'SignUp'
) => {
  const [status, setStatus] = useState<VerificationStatusType>('notExecuted');
  const { unknownError } = useGlobalError();
  const { appLogin } = useAuth();

  const verifyEmail = async (pin: string) => {
    setStatus('inProgress');

    try {
      if (type === 'EmailChange') {
        await userService.confirmEmail(credentials.email, pin);
      } else {
        await userService.confirmSignUp(credentials.email, pin);
        clearLastSignUpEmail();
      }

      setStatus('completed');
    } catch (error: any) {
      if (error.response?.status === 400) {
        setStatus('failed');
      } else {
        setStatus('notExecuted');
        unknownError();
      }
    }
  };

  const confirmVerification = async () => {
    if (!!credentials.password) {
      try {
        const { data: loggedData } = await authService.login(credentials.email, credentials.password);
        appLogin(loggedData.authToken, 'Email');
        onConfirmed();
      } catch (error) {
        unknownError();
      }
    } else {
      onConfirmed();
    }
  };

  const resendCode = async () => {
    try {
      await userService.resendActivationToken(credentials.email);
    } catch (error) {
      unknownError();
    }
  };

  return { status, verifyEmail, resendCode, confirmVerification };
};
